<script lang="ts">
export default {
  name: 'BinderReviewsPage',
};
</script>

<script setup lang="ts">
import { computed, nextTick, onUnmounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { AlertCircle, ArrowLeft, Clock, Loader, Search } from '@lucide/vue';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import Badge from '@/components/ui/Badge.vue';
import UIInput from '@/components/ui/UIInput.vue';
import UISelect from '@/components/ui/UISelect.vue';
import AppButton from '@/components/v2/AppButton.vue';
import PagingControl from '@/components/v2/PagingControl.vue';
import TableSkeleton from '@/components/v2/TableSkeleton.vue';
import { binderReviewService } from '@/services/v2/BinderReviewService';
import { binderReviewRouteQuery } from '@/composables/useBinderReviewSequence';
import { formatDate } from '@/utilities/Format';
import { relativeDateTime } from '@/utilities/Dates';
import {
  PENDING_BINDER_REVIEW_STATUS,
  type BinderReviewItem,
  type BinderReviewQuery,
  type BinderReviewSource,
  type BinderReviewStatus,
} from '@/types/BinderReview';

// Every binder extraction on the client, internal and external, as one triage list. The
// modal this replaced could only show a name and a policy number; the columns here are
// what a reviewer actually sorts on when a client has more waiting than fits on a screen.
const route = useRoute();
const router = useRouter();

const PAGE_SIZE = 25;

// Reviewed has no column of its own: saying "Reviewed" in the status and naming the
// reviewer beside it is one fact, and splitting it across two columns read as two.
const COLUMNS = ['Source', 'Document', 'Policy', 'Effective', 'Uploaded by', 'Scanned', 'Status', ''];

const SOURCE_LABELS: Record<BinderReviewSource, string> = {
  internal: 'Policy',
  external: 'External',
};

// The status says what state a binder is in; opening it is a separate control with one
// verb. The two used to be the same widget, which made "Review" and "View" look like
// different actions when they open the same screen.
const STATUS_LABELS: Record<BinderReviewStatus, string> = {
  ready: 'Needs review',
  reviewed: 'Reviewed',
  processing: 'Scanning',
  failed: 'Failed',
  discarded: 'Dismissed',
};

const STATUS_BADGE_CLASSES: Record<BinderReviewStatus, string> = {
  ready: 'badge-warning',
  reviewed: 'badge-success',
  processing: 'badge-ghost',
  failed: 'badge-error',
  discarded: 'badge-ghost',
};

// `ready` is deliberately absent: as a filter it returned a different set from
// `pending` while reading as the same words. Dismissed binders are external-only and
// terminal, so they show up under All rather than earning their own entry.
const STATUS_OPTIONS = [
  { value: PENDING_BINDER_REVIEW_STATUS, label: 'Needs review' },
  { value: 'processing', label: 'Still scanning' },
  { value: 'reviewed', label: 'Reviewed' },
  { value: 'failed', label: 'Failed' },
  { value: 'all', label: 'All statuses' },
];

const SOURCE_OPTIONS = [
  { value: 'all', label: 'All sources' },
  { value: 'internal', label: 'Policy binders' },
  { value: 'external', label: 'External binders' },
];

const accountId = computed(() => String(route.params.id));

// The route query is the single source of truth, so a refresh or a shared link lands on
// the same view, and a review page opened from here can inherit the same filters.
const statusFilter = computed(() => String(route.query.status ?? PENDING_BINDER_REVIEW_STATUS));
const sourceFilter = computed(() => String(route.query.source ?? ''));
const searchFilter = computed(() => String(route.query.search ?? ''));
const currentPage = computed(() => Math.max(0, Number(route.query.page ?? 0)));
// A document-exception notification links here with the failed binder's document, so
// its row stands out among the client's other failures.
const linkedDocumentId = computed(() => Number(route.query.documentId) || null);

const filters = computed<BinderReviewQuery>(() => ({
  // `all` is the absence of a status filter, which the API spells as no param at all.
  status: statusFilter.value === 'all' ? '' : statusFilter.value,
  source: sourceFilter.value as BinderReviewSource | '',
  search: searchFilter.value,
}));

const items = ref<BinderReviewItem[]>([]);
const total = ref(0);
const isLoading = ref(true);
const loadError = ref(false);

const searchInput = ref(searchFilter.value);
let searchDebounce: ReturnType<typeof setTimeout> | undefined;

const totalPages = computed(() => Math.max(1, Math.ceil(total.value / PAGE_SIZE)));
const hasFilters = computed(
  () => statusFilter.value !== PENDING_BINDER_REVIEW_STATUS || !!sourceFilter.value || !!searchFilter.value,
);

const policiesRoute = computed(() => ({ path: `/account/${accountId.value}/policy-detail` }));

async function load() {
  isLoading.value = true;
  loadError.value = false;
  try {
    const response = await binderReviewService.list(accountId.value, {
      ...filters.value,
      page: currentPage.value,
      size: PAGE_SIZE,
    });
    items.value = response.items;
    total.value = response.total;
    scrollToLinkedRow();
  } catch {
    items.value = [];
    total.value = 0;
    loadError.value = true;
  } finally {
    isLoading.value = false;
  }
}

// Any filter change starts the list over: page 3 of the old filter is meaningless under
// the new one.
function setFilter(patch: Record<string, string>) {
  // The linked document only marks where a notification landed; moving the list on drops it.
  const query = { ...route.query, ...patch, page: undefined, documentId: undefined };
  for (const key of Object.keys(patch)) {
    if (!patch[key]) {
      delete query[key];
    }
  }
  router.replace({ query });
}

function setPage(page: number) {
  router.replace({ query: { ...route.query, page: page > 0 ? String(page) : undefined, documentId: undefined } });
}

function onSearchInput(value: string) {
  searchInput.value = value;
  clearTimeout(searchDebounce);
  searchDebounce = setTimeout(() => setFilter({ search: value.trim() }), 300);
}

// Only a result that exists can be opened; a binder still scanning has nothing to show.
const isOpenable = (item: BinderReviewItem) =>
  item.promptResultId != null && (item.status === 'ready' || item.status === 'reviewed');

// Each source's own stable identity: a submission keeps its id across the scan, while an
// extraction result is what identifies an internal binder's review.
const rowKey = (item: BinderReviewItem) =>
  `${item.source}-${(item.source === 'external' ? item.submissionId : item.promptResultId) ?? item.documentId}`;

const testId = (item: BinderReviewItem) => `open-binder-review-${rowKey(item)}`;
const statusTestId = (item: BinderReviewItem) => `binder-review-status-${rowKey(item)}`;

const isLinked = (item: BinderReviewItem) => item.documentId === linkedDocumentId.value;

const tableScroller = ref<HTMLElement | null>(null);
// Once per visit: later reloads (a filter, a page) are the reviewer moving on.
let hasScrolledToLinkedRow = false;
async function scrollToLinkedRow() {
  if (hasScrolledToLinkedRow || linkedDocumentId.value == null) return;
  await nextTick();
  const row = tableScroller.value?.querySelector('[data-linked-row]');
  if (!row) return;
  hasScrolledToLinkedRow = true;
  row.scrollIntoView({ block: 'center' });
}

function open(item: BinderReviewItem) {
  const query = binderReviewRouteQuery(filters.value);
  router.push(
    item.source === 'external'
      ? {
          name: 'account-external-policy-binder-review',
          params: { id: accountId.value, submissionId: String(item.submissionId) },
          query,
        }
      : {
          name: 'account-binder-review',
          params: { id: accountId.value, promptResultId: String(item.promptResultId) },
          query,
        },
  );
}

watch(
  () => [accountId.value, statusFilter.value, sourceFilter.value, searchFilter.value, currentPage.value],
  load,
  { immediate: true },
);

watch(searchFilter, (value) => {
  if (value !== searchInput.value) {
    searchInput.value = value;
  }
});

onUnmounted(() => clearTimeout(searchDebounce));
</script>

<template>
  <div class="flex h-full min-h-0 flex-col gap-3 p-4">
    <div class="flex shrink-0 flex-wrap items-center gap-3">
      <AppButton
        data-testid="back-to-policies"
        @click="router.push(policiesRoute)">
        <ArrowLeft :size="16" /> Policies
      </AppButton>
      <h1 class="text-2xl">Binder Scans</h1>
      <Badge
        v-if="total > 0"
        variant="secondary"
        data-testid="binder-review-total">
        {{ total }}
      </Badge>
    </div>

    <Card class="flex min-h-0 flex-1 flex-col border-slate-200 bg-white/80 backdrop-blur-sm">
      <CardHeader class="shrink-0 pb-2">
        <div class="flex flex-wrap items-center gap-4 px-2">
          <div class="relative w-72 shrink-0">
            <Search class="pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <UIInput
              :model-value="searchInput"
              type="text"
              placeholder="Search by document or policy"
              class="h-10 border border-slate-400 pl-9"
              data-testid="binder-review-search"
              @update:model-value="onSearchInput(String($event))" />
          </div>

          <div class="ml-auto flex flex-wrap items-center gap-3 pr-2">
            <div class="flex items-center gap-2">
              <label class="text-sm font-medium text-slate-600">Status:</label>
              <UISelect
                :model-value="statusFilter"
                :options="STATUS_OPTIONS"
                class="w-[170px]"
                data-testid="binder-review-status-filter"
                @update:model-value="setFilter({ status: $event })" />
            </div>
            <div class="flex items-center gap-2">
              <label class="text-sm font-medium text-slate-600">Source:</label>
              <UISelect
                :model-value="sourceFilter || 'all'"
                :options="SOURCE_OPTIONS"
                class="w-[170px]"
                data-testid="binder-review-source-filter"
                @update:model-value="setFilter({ source: $event === 'all' ? '' : $event })" />
            </div>
          </div>
        </div>
      </CardHeader>

      <CardContent class="flex min-h-0 flex-1 flex-col">
        <TableSkeleton
          v-if="isLoading && items.length === 0"
          :columns="COLUMNS"
          :show-filter-bar="false"
          :show-title="false" />

        <div
          v-else-if="loadError"
          class="flex items-center justify-center py-8"
          data-testid="binder-review-list-error">
          <AlertCircle class="mr-2 h-6 w-6 text-red-500" />
          <span class="text-red-600">Could not load this client’s binder scans.</span>
        </div>

        <div
          v-else-if="items.length === 0"
          class="py-12 text-center"
          data-testid="binder-review-empty">
          <Clock class="mx-auto mb-4 h-12 w-12 text-slate-300" />
          <h3 class="mb-2 text-lg font-medium text-slate-700">No Binder Scans</h3>
          <p class="mx-auto max-w-sm text-sm text-slate-500">
            {{
              hasFilters
                ? 'No scans match these filters. Try widening them.'
                : 'This client has no binders waiting to be reviewed.'
            }}
          </p>
        </div>

        <div
          v-else
          ref="tableScroller"
          class="min-h-0 flex-1 overflow-auto">
          <table
            class="apptable dense --tight-first"
            data-testid="binder-review-table">
            <!-- apptable styles the header off `thead > tr > td`, not `th`. -->
            <thead class="sticky top-0 z-40">
              <tr>
                <td
                  v-for="column in COLUMNS"
                  :key="column"
                  class="text-left">
                  {{ column }}
                </td>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="item in items"
                :key="rowKey(item)"
                :class="{ 'bg-blue-100': isLinked(item) }"
                :data-linked-row="isLinked(item) || undefined"
                :data-testid="`binder-review-row-${rowKey(item)}`">
                <td class="text-left">
                  <span
                    class="badge badge-sm"
                    :class="item.source === 'external' ? 'badge-warning' : 'badge-ghost'"
                    :data-testid="`binder-review-source-${item.source}`">
                    {{ SOURCE_LABELS[item.source] }}
                  </span>
                </td>
                <td class="max-w-[22rem] truncate text-left font-medium">{{ item.documentName }}</td>
                <td class="text-left">{{ item.policyNumber || '--' }}</td>
                <td class="text-left">{{ item.policyEffectiveDate ? formatDate(item.policyEffectiveDate) : '--' }}</td>
                <td class="text-left">{{ item.uploadedBy || '--' }}</td>
                <td
                  class="text-left"
                  :title="item.scannedAt ? formatDate(item.scannedAt) : ''">
                  {{ item.scannedAt ? relativeDateTime(item.scannedAt) : '--' }}
                </td>
                <td class="text-left">
                  <span
                    class="badge badge-sm gap-1 whitespace-nowrap"
                    :class="STATUS_BADGE_CLASSES[item.status]"
                    :title="item.errorMessage"
                    :data-testid="statusTestId(item)">
                    <Loader
                      v-if="item.status === 'processing'"
                      class="h-3 w-3 animate-spin" />
                    {{ STATUS_LABELS[item.status] }}
                  </span>
                  <!-- Indented past the badge's own padding so it sits under the label
                       rather than under the badge's left edge. -->
                  <span
                    v-if="item.reviewedBy"
                    class="block pl-2 text-xs whitespace-nowrap text-base-content/60">
                    {{ item.reviewedBy }} · {{ relativeDateTime(item.reviewedAt) }}
                  </span>
                </td>
                <td class="text-left">
                  <AppButton
                    v-if="isOpenable(item)"
                    outline
                    success
                    :data-testid="testId(item)"
                    @click="open(item)">
                    Open
                  </AppButton>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <PagingControl
          v-if="totalPages > 1"
          :current-page="currentPage + 1"
          :total-pages="totalPages"
          @set-page="setPage($event - 1)"
          @previous-page="setPage(currentPage - 1)"
          @next-page="setPage(currentPage + 1)" />

        <div
          v-if="isLoading && items.length > 0"
          class="flex items-center justify-center gap-2 py-2 text-sm text-slate-500">
          <Loader class="h-4 w-4 animate-spin" />
          <span>Loading…</span>
        </div>
      </CardContent>
    </Card>
  </div>
</template>
